/**
 * OpenPGP Key Status Card Component
 *
 * Displays the current status of an OpenPGP key and allows configuration
 */

import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  makeStyles
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Alert, AlertTitle } from '@material-ui/lab';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/Help';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2)
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  statusIcon: {
    marginRight: theme.spacing(2)
  },
  keyInfo: {
    flexGrow: 1
  },
  keyInfoBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  warningAlert: {
    marginTop: theme.spacing(2)
  },
  algorithmSelect: {
    marginTop: theme.spacing(2)
  },
  keyInfoLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  }
}));

/**
 * Algorithm options for OpenPGP key generation
 */
const OPENPGP_ALGORITHMS = [
  { value: 'rsa2048', label: 'RSA-2048', description: 'Widely compatible, recommended' },
  { value: 'rsa3072', label: 'RSA-3072', description: 'Higher security' },
  { value: 'rsa4096', label: 'RSA-4096', description: 'Maximum RSA security' },
  { value: 'nistp256', label: 'ECC P-256', description: 'Modern, efficient' },
  { value: 'nistp384', label: 'ECC P-384', description: 'High security elliptic curve' },
  { value: 'nistp521', label: 'ECC P-521', description: 'Maximum ECC security' }
];

function OpenPGPKeyStatusCard({ keyType, status, config, onConfigChange }) {
  const classes = useStyles();

  /**
   * Get icon based on key status
   */
  const getStatusIcon = () => {
    const iconProps = { fontSize: 'large', className: classes.statusIcon };

    switch (status.status) {
      case 'configured':
        return <CheckCircleIcon {...iconProps} style={{ color: '#4caf50' }} />;
      case 'empty':
        return <RadioButtonUncheckedIcon {...iconProps} style={{ color: '#9e9e9e' }} />;
      case 'error':
        return <ErrorIcon {...iconProps} style={{ color: '#f44336' }} />;
      default:
        return <HelpIcon {...iconProps} />;
    }
  };

  /**
   * Get chip color based on status
   */
  const getStatusColor = () => {
    switch (status.status) {
      case 'configured':
        return 'primary';
      case 'empty':
        return 'default';
      case 'error':
        return 'secondary';
      default:
        return 'default';
    }
  };

  /**
   * Get human-readable status label
   */
  const getStatusLabel = () => {
    const labels = {
      'configured': 'Configured',
      'empty': 'Empty',
      'error': 'Error'
    };
    return labels[status.status] || 'Unknown';
  };

  return (
    <Card variant="outlined" className={classes.card}>
      <CardContent>
        {/* Status Header */}
        <Box className={classes.statusHeader}>
          {getStatusIcon()}
          <Box className={classes.keyInfo}>
            <Typography variant="h6">{keyType.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {keyType.description}
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel()}
            color={getStatusColor()}
            size="small"
          />
        </Box>

        {/* Current Key Information */}
        {status.status === 'configured' && status.keyInfo && !status.keyInfo.error && (
          <Box className={classes.keyInfoBox}>
            <Typography variant="subtitle2" className={classes.keyInfoLabel}>
              Current Key
            </Typography>
            <Typography variant="body2">
              <strong>Algorithm:</strong> {status.keyInfo.algorithm || 'Unknown'}
            </Typography>
            <Typography variant="body2">
              <strong>Key Size:</strong> {status.keyInfo.keySize || 'Unknown'}
            </Typography>
            <Typography variant="body2">
              <strong>Fingerprint:</strong> {status.keyInfo.fingerprint || 'N/A'}
            </Typography>
          </Box>
        )}

        {/* Key Attributes */}
        {status.status === 'configured' && status.keyAttributes && !status.keyAttributes.error && (
          <Box className={classes.keyInfoBox}>
            <Typography variant="subtitle2" className={classes.keyInfoLabel}>
              Key Attributes
            </Typography>
            <Typography variant="body2">
              <strong>Algorithm:</strong> {status.keyAttributes.algorithm}
            </Typography>
            <Typography variant="body2">
              <strong>Size:</strong> {status.keyAttributes.keySize}
            </Typography>
          </Box>
        )}

        {/* Error Status */}
        {status.status === 'error' && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            <AlertTitle>Error</AlertTitle>
            {status.error || 'Failed to check key status'}
          </Alert>
        )}

        {/* Action Selection */}
        <FormControl fullWidth variant="outlined">
          <InputLabel>Action</InputLabel>
          <Select
            value={config.mode}
            onChange={(e) => onConfigChange(keyType.id, 'mode', e.target.value)}
            label="Action"
          >
            <MenuItem value="skip">
              Skip (Keep Current)
            </MenuItem>
            <MenuItem value="generate">
              {status.status === 'empty' ? 'Generate New Key' : 'Replace with New Key'}
            </MenuItem>
            <MenuItem value="import">
              {status.status === 'empty' ? 'Import External Key' : 'Replace with Imported Key'}
            </MenuItem>
          </Select>
        </FormControl>

        {/* Key Replacement Warning */}
        {config.mode !== 'skip' && status.status === 'configured' && (
          <Alert severity="warning" className={classes.warningAlert}>
            <AlertTitle>Key Replacement Warning</AlertTitle>
            This action will permanently replace the existing OpenPGP key.
            Any data encrypted with the current key will become inaccessible.
            Make sure you have backed up your key and any important data.
          </Alert>
        )}

        {/* Algorithm Selection for Generation */}
        {config.mode === 'generate' && (
          <FormControl fullWidth variant="outlined" className={classes.algorithmSelect}>
            <InputLabel>Key Algorithm</InputLabel>
            <Select
              value={config.algorithm}
              onChange={(e) => onConfigChange(keyType.id, 'algorithm', e.target.value)}
              label="Key Algorithm"
            >
              {OPENPGP_ALGORITHMS.map(alg => (
                <MenuItem key={alg.value} value={alg.value}>
                  {alg.label} - {alg.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </CardContent>
    </Card>
  );
}

export default OpenPGPKeyStatusCard;
